import React from "react";
import SVG from "react-inlinesvg";
import { Modal, Tabs, Tab } from "react-bootstrap";
import { formatAmount, formatDate, formatStatus, getOrderBalanceAfter, statusColourName, transactionCat, transactionType, truncateString, ucFirst, userProfileLink } from "../../../../util/Helpers";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import {  useGetActivityDetailsQuery, useGetStalledTransactionsQuery, useGetTransactionAccountDetailsQuery } from "../../../../../redux/apiSlice";
import { CardHeader } from "../../../../../_metronic/_partials/controls";
import DynamicSimpleTable from "../../../../util/DynamicSimpleReactTable";


const ViewStalledTransactionModal = ({ showViewStalledTransactionModal, details, onHide }) => {


    // Fetch stalled transactions using the flaggedId from details
    const { data: stalledTransactions } = useGetStalledTransactionsQuery(details?.flaggedId);
    const { data: accountDetails } = useGetTransactionAccountDetailsQuery({
        userId: details?.userId,
        accountId: details?.accountId,
    });
    const { data: activityDetails } = useGetActivityDetailsQuery({
        userId: details?.userId,
        query: details?.flaggedId,
    });

    const state = details?.state.map((row, i) => (
        <div key={`state-${i}`} className="timeline-item align-items-start">
            {/* Badge */}
            <div className="timeline-badge">
                <i className={`fa fa-genderless text-${statusColourName(row.status)} icon-xl`}></i>
            </div>

            {/* Content */}
            <div className="timeline-content d-flex flex-column pl-3">
                <span className="font-weight-bolder text-dark-75 font-size-lg">{row.status}</span>

                {!row.resolutionComment && row.comment && (
                    <span className="text-muted font-size-md mt-1">
                        <strong>Comment:</strong> {row.comment}
                    </span>
                )}

                {row.resolutionComment && (
                    <span className="text-muted font-size-md mt-1">
                        <strong>Resolution Comment:</strong> {row.resolutionComment}
                    </span>
                )}

                {/* Hide Resolution Type if status is "pending_review" */}
                {row.resolutionType && row.status !== "pending_review" && (
                    <span className="text-muted font-size-md">
                        <strong>Resolution Type:</strong> {row.resolutionType}
                    </span>
                )}

                <span className="text-muted font-size-md">
                    <strong>Date:</strong> {formatDate(row.updated)}
                </span>

            </div>
        </div>
    ));


    const totalEquity = accountDetails?.positions?.reduce(
        (total, position) => total + position.equityBalance,
        0
    ) || 0;
    const formatActivity = row => {
        return <div className="d-flex align-items-center">
            <div className="symbol symbol-50 flex-shrink-0 mr-4">
                <span className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {transactionType(row)}
                    <span className="text-muted pl-2">
                        {transactionCat(row)}
                    </span>
                </span>
            </div>
        </div>
    }

    const formatOrderActivity = row => {
        return <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
            <span className={`svg-icon svg-icon-lg svg-icon-${row.side === "BUY" ? "primary" : "danger"} mr-3`}>
                <SVG
                    title=""
                    src={toAbsoluteUrl("/media/svg/icons/Shopping/Cart2.svg")}
                />
            </span>
            <div>
                <span className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {row.symbol}
                    <span className="text-muted pl-2">
                        - {row.side}
                    </span>
                </span>
            </div>
        </div>
    }
   

    return (
        <Modal
            show={showViewStalledTransactionModal}
            onHide={onHide}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
        >
            <Modal.Header>
                <Modal.Title id="contained-modal-title-vcenter">
                    <span
                        className={`svg-icon svg-icon-lg svg-icon-${details?.type === "DEPOSIT" ? "primary" : "danger"} mr-3`}>
                        <SVG
                            title=""
                            src={toAbsoluteUrl("/media/svg/icons/Shopping/Gift.svg")}
                        />
                    </span>
                    Stalled Transaction Details
                </Modal.Title>
                <button type="button" className="close" onClick={onHide}>
                    <span aria-hidden="true" className="d-block">×</span>
                    <span className="sr-only">Close</span>
                </button>
            </Modal.Header>
            <Modal.Body className="px-10 pt-2 pb-5">
                <div className="card card-custom gutter-b">
                    <div className="row p-8">
                        <div className="d-flex flex-column flex-grow-1 mt-0 pr-3 col-md">
                            <span className="font-weight-bolder font-size-lg h5 text-primary">Market Value</span>
                            <span className="text-muted font-weight-bold font-size-md my-1">
                                {accountDetails?.currency} {totalEquity.toFixed(2)}
                            </span>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 mt-0 pr-3 col-md">
                            <span className="font-weight-bolder font-size-lg h5 text-primary">Cash Balance</span>
                            <span className="text-muted font-weight-bold font-size-md my-1">
                                {accountDetails?.currency} {accountDetails?.cashBalance?.toFixed(2)}
                            </span>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 mt-0 pr-3 col-md">
                            <span className="font-weight-bolder font-size-lg h5 text-primary">Total Value</span>
                            <span className="text-muted font-weight-bold font-size-md my-1">
                                {accountDetails?.currency} {(totalEquity + (accountDetails?.cashBalance || 0)).toFixed(2)}
                            </span>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 mt-0 pr-3 col-md">
                            <span className="font-weight-bolder font-size-lg h5 text-primary">Pending Settlement</span>
                            <span className="text-muted font-weight-bold font-size-md my-1">
                                {accountDetails?.currency} {accountDetails?.pendingSettlementAmount?.toFixed(2)}
                            </span>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 mt-0 pr-3 col-md">
                            <span className="font-weight-bolder font-size-lg h5 text-primary">Trading Power</span>
                            <span className="text-muted font-weight-bold font-size-md my-1">
                                {accountDetails?.currency} {accountDetails?.tradingPower?.toFixed(2)}
                            </span>

                        </div>
                    </div>
                </div>

                <Tabs defaultActiveKey="details" id="stalled-transaction-tabs">
                    {/* Details Tab */}
                    <Tab eventKey="details" title="Details">
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">AutoId</h6>
                                <span className="text-muted">{stalledTransactions?.autoId}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Account Balance</h6>
                                <span className="text-muted">{formatAmount(stalledTransactions?.currency, stalledTransactions?.accountBal)}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Description</h6>
                                <span className="text-muted">{stalledTransactions?.comment}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">Currency</h6>
                                <span className="text-muted">{stalledTransactions?.currency}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Transaction Type</h6>
                                <span className="text-muted">{stalledTransactions?.tranType}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Account Id</h6>
                                <span className="text-muted">{stalledTransactions?.accountId}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">Transaction Time</h6>
                                <span className="text-muted">{stalledTransactions?.tranTime}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">User Id</h6>
                                <span className="text-muted">{stalledTransactions?.userId}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Partner</h6>
                                <span className="text-muted">{stalledTransactions?.partner}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">Transaction Amount</h6>
                                <span className="text-muted">{formatAmount(stalledTransactions?.currency, stalledTransactions?.tranAmount)}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Resolution Type</h6>
                                <span className="text-muted">{details?.resolutionType}</span>
                            </div>

                            <div className="col-lg py-4">
                                <h6 className=" px">Transaction Status</h6>
                                <span className="text-muted">{stalledTransactions?.tranStatus}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">Correction Amount</h6>
                                <span className="text-muted">{details?.cashCorrectionAmount}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Correction Quantity</h6>
                                <span className="text-muted">{details?.positionCorrectionQuantity}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Email</h6>
                                <span className="text-muted">{accountDetails?.payments?.[0]?.email || "N/A"}</span>

                            </div>


                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <h6 className=" px">Created</h6>
                                <span className="text-muted">{formatDate(details?.created)}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className=" px">Updated</h6>
                                <span className="text-muted">{formatDate(stalledTransactions?.currency, details?.updated)}</span>
                            </div>
                            <div className="col-lg py-4">
                                <h6 className="px">Flagged Id</h6>
                                <span className="text-muted">{details?.flaggedId}</span>
                            </div>
                            


                        </div>
                        <div className="row">
                            <div className="col-lg py-4">
                                <a
                                    href={userProfileLink(stalledTransactions?.userId, stalledTransactions?.accountId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary"
                                >
                                    View User's Profile
                                </a>


                            </div>
                            <div className="col-lg py-4">
                                <h6 className="px">Source</h6>
                                <span className="text-muted">{activityDetails?.data?.[0]?.source}</span>
                            </div>
                           


                        </div>
                        <h4 className="mt-5 text-primary">Flagged Transaction Progress</h4>

                        {
                            state?.length === 0
                                ? <span className="text-muted">Nil</span>
                                : <div className="timeline timeline-7 mt-5">{state}</div>
                        }

                        <div className="row">
                            <div className="col-lg">
                                <h4 className="mb-4 pt-5 mt-5 text-muted h5">More Details</h4>
                                <div className="text-muted bg-light-secondary rounded-lg p-4 overflow-auto" style={{ maxHeight: "300px" }}>
                                    <pre className="mb-0">{JSON.stringify(stalledTransactions, null, 4)}</pre>
                                </div>
                            </div>
                        </div>
                    </Tab>
                    {/* Activities Tab */}
                    <Tab eventKey="activities" title="Activity">
                        <div className="row">
                            <div className="col-12">
                                {/* <!--begin::List Widget 14--> */}
                                <div className="card card-custom gutter-b">

                                    <CardHeader title={`Activity`}>
                                    </CardHeader>
                                    <div className="card-body pt-0">
                                        <DynamicSimpleTable
                                            queryParams={
                                                `userId=${details?.userId}`
                                                + `&query=${details?.flaggedId}`
                                            }
                                            searchBox={true}
                                            source={{ url: `/transactions` }}
                                            columns={[
                                                { title: "ACTIVITY", format: formatActivity },
                                                { title: "DATE", format: row => formatDate(row.created) },
                                                { title: "REFERENCE", format: row => truncateString(row.refId || row.autoId, 18) },
                                                { title: "DESCRIPTION", key: "comment" },
                                                { title: "AMOUNT", format: row => formatAmount(row.currency, row.tranAmount) },
                                                { title: "STATUS", format: row => formatStatus(ucFirst(row.tranStatus)) },
                                                { title: "SOURCE", key: "source" },
                                                { title: "DESTINATION", key: "destination" },
                                            ]}
                                            refreshIcon={true}
                                        />
                                    </div>
                                    {/* <!--end::Body--> */}
                                </div>
                                {/* <!--end::List Widget 14--> */}
                            </div>
                        </div>
                    </Tab>
                    {details?.stalledTransactionType
                        === "DEPOSIT" || details?.stalledTransactionType === "WITHDRAW" ? (
                        <Tab eventKey="payments" title="Payments">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card card-custom gutter-b">
                                        <CardHeader title={`Payments`} />
                                        <div className="card-body pt-0">
                                            <DynamicSimpleTable
                                                queryParams={`query=${details?.userId}`}
                                                searchBox={true}
                                                source={{ url: `/payments` }}
                                                columns={[
                                                    { title: "USERNAME", key: "username" },
                                                    { title: "DATE", format: row => formatDate(row.createdAt) },
                                                    { title: "REFERENCE", format: row => truncateString(row.ref || row.autoId, 18) },
                                                    { title: "TYPE", key: "type" },
                                                    { title: "AMOUNT", format: row => formatAmount(row.currency, row.total) },
                                                    { title: "STATUS", format: row => formatStatus(ucFirst(row.status)) },
                                                    { title: "METHOD", key: "method" },
                                                    { title: "DESTINATION", key: "destination" },
                                                ]}
                                                refreshIcon={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Tab>
                    ) : null}

                    {details?.stalledTransactionType === "BUY" || details?.stalledTransactionType === "SELL" ? (
                        <Tab eventKey="orders" title="Orders">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card card-custom gutter-b">
                                        <CardHeader title={`Orders`} />
                                        <div className="card-body pt-0">
                                            <DynamicSimpleTable
                                                queryParams={
                                                    `userGroup=${stalledTransactions?.userGroup}`
                                                    + `&liveData=true`
                                                    + `&userId=${stalledTransactions?.userId}`
                                                    + `&provider=${stalledTransactions?.partner}`
                                                }
                                                searchBox={true}
                                                source={{ url: `/orders` }}
                                                columns={[
                                                    { title: "USERNAME", key: "username" },
                                                    { title: "ACTIVITY", format: formatOrderActivity },
                                                    { title: "ID", key: "id" },
                                                    { title: "REFERENCE", key: "refid" },
                                                    { title: "QUANTITY", key: "quantity" },
                                                    { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                                    { title: "FEE", format: row => formatAmount(row.currency, Math.abs(row.total - row.amount)) },
                                                    { title: "TOTAL", format: row => formatAmount(row.currency, row.total) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBalance) },
                                                    { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getOrderBalanceAfter(row)) },
                                                    { title: "DATE", format: row => formatDate(row.created) },
                                                ]}
                                                refreshIcon={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Tab>
                    ) : null}

                    <Tab eventKey="transactions" title="Transactions">
                        <div className="row">
                            <div className="col-12">
                                <div className="card card-custom gutter-b">
                                    <CardHeader title={`Transactions`} />
                                    <div className="card-body pt-0">
                                        <DynamicSimpleTable
                                            queryParams={`userId=${details?.userId}`}
                                            searchBox={true}
                                            source={{ url: `/transactions` }}
                                            columns={[
                                                { title: "ACTIVITY", format: formatActivity },
                                                { title: "DATE", format: row => formatDate(row.created) },
                                                { title: "REFERENCE", format: row => truncateString(row.refId || row.autoId, 18) },
                                                { title: "DESCRIPTION", key: "comment" },
                                                { title: "AMOUNT", format: row => formatAmount(row.currency, row.tranAmount) },
                                                { title: "STATUS", format: row => formatStatus(ucFirst(row.tranStatus)) },
                                                { title: "SOURCE", key: "source" },
                                                { title: "DESTINATION", key: "destination" },
                                            ]}
                                            refreshIcon={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    onClick={onHide}
                    className="btn btn-secondary btn-elevate px-5"
                >
                    Okay
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ViewStalledTransactionModal;
